import { AppLayout } from "@bcc/components/app-layout";
import { DashboardView } from "@bcc/components/views/dashboard-view";
import { useTranslation } from "react-i18next";

const salesTrendData = {
  sevenDay: [
    { date: "Mon", sales: 4200 },
    { date: "Tue", sales: 3800 },
    { date: "Wed", sales: 5100 },
    { date: "Thu", sales: 4900 },
    { date: "Fri", sales: 5400 },
    { date: "Sat", sales: 5200 },
    { date: "Sun", sales: 5800 },
  ],
  thirtyDay: [
    { date: "Week 1", sales: 28500 },
    { date: "Week 2", sales: 31200 },
    { date: "Week 3", sales: 29800 },
    { date: "Week 4", sales: 32400 },
  ],
};

const ordersByProductData = [
  {
    product: "Espresso Blend",
    orders: 850,
    color: "#3b82f6",
  },
  {
    product: "Single Origin",
    orders: 620,
    color: "#8b5cf6",
  },
  {
    product: "Decaf Medium",
    orders: 480,
    color: "#ec4899",
  },
  {
    product: "Light Roast",
    orders: 340,
    color: "#f97316",
  },
  {
    product: "Dark Roast",
    orders: 210,
    color: "#06b6d4",
  },
];

export function DashboardPage() {
  const { t } = useTranslation();

  const alertsData = [
    {
      id: 1,
      type: "warning" as const,
      icon: "alert-triangle",
      title: t("dashboard.alerts.slaPerformanceDip.title"),
      message: t("dashboard.alerts.slaPerformanceDip.message"),
      primaryAction: t("dashboard.alerts.slaPerformanceDip.primaryAction"),
      primaryLink: "/sla-details",
      secondaryAction: t("dashboard.alerts.secondaryActions.dismiss"),
      bgColor: "bg-yellow-50",
      iconColor: "text-yellow-600",
      primaryButtonColor: "bg-yellow-600",
    },
    {
      id: 2,
      type: "error" as const,
      icon: "alert-circle",
      title: t("dashboard.alerts.inventoryAlert.title"),
      message: t("dashboard.alerts.inventoryAlert.message"),
      primaryAction: t("dashboard.alerts.inventoryAlert.primaryAction"),
      primaryLink: "/inventory",
      secondaryAction: t("dashboard.alerts.secondaryActions.ignore"),
      bgColor: "bg-red-50",
      iconColor: "text-red-600",
      primaryButtonColor: "bg-red-600",
    },
    {
      id: 3,
      type: "success" as const,
      icon: "check-circle",
      title: t("dashboard.alerts.orderMilestone.title"),
      message: t("dashboard.alerts.orderMilestone.message"),
      primaryAction: t("dashboard.alerts.orderMilestone.primaryAction"),
      primaryLink: "/achievements",
      secondaryAction: t("dashboard.alerts.secondaryActions.close"),
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
      primaryButtonColor: "bg-green-600",
    },
    {
      id: 4,
      type: "info" as const,
      icon: "info",
      title: t("dashboard.alerts.systemUpdate.title"),
      message: t("dashboard.alerts.systemUpdate.message"),
      primaryAction: t("dashboard.alerts.systemUpdate.primaryAction"),
      primaryLink: "/settings/system",
      secondaryAction: t("dashboard.alerts.secondaryActions.later"),
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
      primaryButtonColor: "bg-blue-600",
    },
    {
      id: 5,
      type: "warning" as const,
      icon: "alert-triangle",
      title: t("dashboard.alerts.shippingDelay.title"),
      message: t("dashboard.alerts.shippingDelay.message"),
      primaryAction: t("dashboard.alerts.shippingDelay.primaryAction"),
      primaryLink: "/orders",
      secondaryAction: t("dashboard.alerts.secondaryActions.skip"),
      bgColor: "bg-yellow-50",
      iconColor: "text-yellow-600",
      primaryButtonColor: "bg-yellow-600",
    },
    {
      id: 6,
      type: "error" as const,
      icon: "alert-circle",
      title: t("dashboard.alerts.paymentError.title"),
      message: t("dashboard.alerts.paymentError.message"),
      primaryAction: t("dashboard.alerts.paymentError.primaryAction"),
      primaryLink: "/payments",
      secondaryAction: t("dashboard.alerts.secondaryActions.investigate"),
      bgColor: "bg-red-50",
      iconColor: "text-red-600",
      primaryButtonColor: "bg-red-600",
    },
  ];

  const statsData = [
    {
      name: t("dashboard.stats.salesToday"),
      baseValue: 45000,
      value: "$48,574",
      change: "+5.2%",
      changeType: "positive" as const,
      description: t("dashboard.stats.vsYesterday"),
      icon: "dollar-sign",
      sparklineData: [
        { day: 1, value: 4200 },
        { day: 2, value: 3800 },
        { day: 3, value: 5100 },
        { day: 4, value: 4900 },
        { day: 5, value: 5400 },
        { day: 6, value: 5200 },
        { day: 7, value: 5800 },
      ],
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      sla: {
        score: 92,
        impact: 0.95,
        status: "excellent" as const,
        message: "Excellent SLA performance is driving conversions up.",
      },
    },
    {
      name: "Orders Pending",
      baseValue: 1800,
      value: "1,847",
      change: "+2.6%",
      changeType: "positive" as const,
      description: t("dashboard.stats.vsYesterday"),
      icon: "shopping-cart",
      sparklineData: [
        { day: 1, value: 250 },
        { day: 2, value: 240 },
        { day: 3, value: 280 },
        { day: 4, value: 260 },
        { day: 5, value: 290 },
        { day: 6, value: 270 },
        { day: 7, value: 300 },
      ],
      color: "text-green-600",
      bgColor: "bg-green-50",
      sla: {
        score: 89,
        impact: 0.87,
        status: "fair" as const,
        message: "3 orders delayed beyond SLA target.",
      },
    },
    {
      name: "Active Products",
      baseValue: 120,
      value: "127",
      change: "+5.8%",
      changeType: "positive" as const,
      description: t("dashboard.stats.vsYesterday"),
      icon: "package",
      sparklineData: [
        { day: 1, value: 115 },
        { day: 2, value: 116 },
        { day: 3, value: 118 },
        { day: 4, value: 120 },
        { day: 5, value: 122 },
        { day: 6, value: 124 },
        { day: 7, value: 127 },
      ],
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      sla: {
        score: 88,
        impact: 0.92,
        status: "fair" as const,
        message: "2 SKUs paused due to SLA risk.",
      },
    },
    {
      name: "Next Payout",
      baseValue: 3200,
      value: "$12,450",
      change: "+9.1%",
      changeType: "positive" as const,
      description: "in 3 days",
      icon: "wallet",
      sparklineData: [
        { day: 1, value: 8500 },
        { day: 2, value: 8800 },
        { day: 3, value: 9200 },
        { day: 4, value: 9100 },
        { day: 5, value: 10200 },
        { day: 6, value: 11100 },
        { day: 7, value: 12450 },
      ],
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      sla: {
        score: 95,
        impact: 1.01,
        status: "excellent" as const,
        message: "Payout adjusted by SLA performance (+1.0%).",
      },
    },
  ];

  return (
    <AppLayout>
      <DashboardView
        alertsData={alertsData}
        statsData={statsData}
        salesTrendData={salesTrendData}
        ordersByProductData={ordersByProductData}
      />
    </AppLayout>
  );
}
